import {React, useContext, Link, useLocation} from 'external/react'
import { clsx, styled, Menu, MenuItem, FormControlLabel, Checkbox, ArrowDropDownIcon } from 'external/material'
import { formatDate } from 'external/date-fns'
import {TeacherContext} from 'teacher/context'
import create from 'helpers/command'
import {displayPoints} from 'helpers/gradebook'

const StyledCellOptions = styled('span')(gradeCellOptionStyles)

export default function GradeCellOptions ({student, column}) {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)

  const handleOpenOptions = event => setAnchorEl(event.currentTarget)
  const handleCloseOptions = () => setAnchorEl(null)

  const options = createColumnOptions(column)

  return (
    <StyledCellOptions className="options">
        <span className={clsx('optionsButton', {"not-assigned": !column.assigned})} onClick={handleOpenOptions}>
          <ArrowDropDownIcon fontSize="small" />
        </span>
        <Menu
          id="long-menu"
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={handleCloseOptions}
        >
          {options.map(({selected, Option, grade, disabled}, index) => (
            <MenuItem key={index} selected={selected} disabled={disabled} onClick={handleCloseOptions}>
              <Option selected={selected} student={student} column={column} grade={grade} />
            </MenuItem>
          ))}
      </Menu>
    </StyledCellOptions>
  )
}

function createColumnOptions(column) {

  const {selectedAnswer, type, external, grades = []} = column
  const {properties = {}} = selectedAnswer || {}
  const disabled = properties.published 

  const gradeOptions = grades.map(grade => {
    const selected = column.selectedAnswer && (column.selectedAnswer.answeredAssessment == grade.answeredAssessment)
    return {selected, Option: GradeOption, grade, disabled}
  })
  return external
    ? [{Option: PublishExternalGrade}, {Option: IgnoreExternalGrade}]
    : type == 'homework'
      ? [{Option: PublishHomework}, {Option: IgnoreGrade}]
      : [{Option: PublishGrade}, {Option: IgnoreGrade}].concat(gradeOptions)
}

function GradeOption ({selected, grade}) {
  const {link, translate, updateState, rollbookContextGrades, query} = useContext(TeacherContext)
  const location = useLocation()

  function handleChange(e) {
    if (selected) return
    const index = rollbookContextGrades.items.findIndex(g => g.answeredAssessment == grade.answeredAssessment)
    if (index < 0) return
    const selectedIndex = rollbookContextGrades.items.findIndex(g => g.properties?.selected)
    
    const update = { [index]: {properties: {selected: {$set: true}}} }
    if (selectedIndex >= 0) {
      update[selectedIndex] = {properties: {selected: {$set: false}}}
    }
    
    updateState({rollbookContextGrades: {items: update}})
    create('grading', 'selectStudentAssessment')({assessment: grade.answeredAssessment})
  }

  const dateFormat = 'yyyy-MM-dd'
  const label = `${displayPoints(grade)} - ${formatDate(grade.started || grade.start, dateFormat)}`

  const reviewPath='/:languageId/:role/:page(quiz)/:assessment/:action(review)'
  const reviewLink = link({page: 'quiz', assessment: grade.answeredAssessment, action: 'review'}, reviewPath)

  function reviewClickHandler() {
    updateState({quizEditFrom: {$set: location.pathname}, quizEditFromQuery: {$set: query}})
  }

  return (
    <FormControlLabel
      control={<Checkbox color="primary" checked={selected} onChange={handleChange} />}
      label={<Link onClick={reviewClickHandler} to={reviewLink} className={clsx('fill', 'centered', 'link')}>{label}</Link>}
    />
  )
}

function PublishGrade (props) {

  const {column = {}} = props
  const {translate, updateState, rollbookContextGrades} = useContext(TeacherContext)
  const {selectedAnswer} = column
  const {properties = {}} = selectedAnswer || {}

  function handleChange(e) {
    const published = e.target.checked
    if (!selectedAnswer) return
    const index = rollbookContextGrades.items.findIndex(g => g.answeredAssessment == selectedAnswer.answeredAssessment)
    if (index < 0) return
    updateState({rollbookContextGrades: {items: {
      [index]: {properties: {published: {$set: published}}},
    }}})
    create('grading', 'publishStudentAssessment')({assessment: selectedAnswer.answeredAssessment, published})
  }

  return (<FormControlLabel
    control={<Checkbox color="primary" checked={properties.published || false} onChange={handleChange} />}
    label={translate(properties.published ? 'grade-published' : 'publish-grade')}
  />)
}

function IgnoreGrade (props) {
  const {student = {}, column = {}} = props
  const context = useContext(TeacherContext) 
  const {translate, updateState} = context 
  const {id, properties: {ignore = false}} = (context.rollbookContextStudentAssignmentProperties[student.id] || {})[column.id] || {properties: {}}

  function handleChange(e) {
    if (!id) return
    const ignore = e.target.checked
    updateState({rollbookContextStudentAssignmentProperties: {[student.id]: {[column.id]: {properties: {ignore: {$set: ignore}}}}}})
    const schema = column.type == 'homework' ? 'homework' : 'quiz'
    create(schema,'ignoreStudentAssignment')({id, ignore})
  }

  return (<FormControlLabel
    control={<Checkbox color="primary" checked={ignore} onChange={handleChange} />}
    label={translate('ignore-grade')}
  />)
}

function IgnoreExternalGrade (props) {
  const {student = {}, column = {}} = props
  const context = useContext(TeacherContext) 
  const {translate, updateState} = context 
  const {id, properties: {ignore = false}} = (context.rollbookContextStudentAssignmentProperties[student.id] || {})[column.id] || {properties: {}}

  async function handleChange(e) {
    const ignore = e.target.checked
    const update = create('grading','ignoreExternalGrade')({assignment: column.id, student: student.id, id, ignore})
    if (id) {
      updateState({rollbookContextStudentAssignmentProperties: {[student.id]: {[column.id]: {properties: {ignore: {$set: ignore}}}}}})
    } else {
      const [assignment] = await update
      updateState({rollbookContextStudentAssignmentProperties: {$auto: {[student.id]: {$auto: {[assignment.assignment]: {$set: assignment}}}}}})
    }
  }

  return (<FormControlLabel
    control={<Checkbox color="primary" checked={ignore} onChange={handleChange} />}
    label={translate('ignore-grade')}
  />)
}

function PublishExternalGrade (props) {

  const {student = {}, column = {}} = props

  const context = useContext(TeacherContext) 
  const {translate, updateState} = context 
  const {id, properties: {published = false}} = (context.rollbookContextStudentAssignmentProperties[student.id] || {})[column.id] || {properties: {}}

  async function handleChange(e) {
    const published = e.target.checked
    const update = create('grading','publishExternalGrade')({assignment: column.id, student: student.id, id, published})
    if (id) {
      updateState({rollbookContextStudentAssignmentProperties: {[student.id]: {[column.id]: {properties: {published: {$set: published}}}}}})
    } else {
      const [assignment] = await update
      updateState({rollbookContextStudentAssignmentProperties: {$auto: {[student.id]: {$auto: {[assignment.assignment]: {$set: assignment}}}}}})
    }
  }

  return (<FormControlLabel
    control={<Checkbox color="primary" checked={published || false} onChange={handleChange} />}
    label={translate(published ? 'grade-published' : 'publish-grade')}
  />)
}

function PublishHomework (props) {
  const {student = {}, column = {}} = props
  const context = useContext(TeacherContext) 
  const {translate, updateState} = context 
  const {id, properties: {published = false}} = (context.rollbookContextStudentAssignmentProperties[student.id] || {})[column.id] || {properties: {}}

  function handleChange(e) {
    if (!id) return
    const published = e.target.checked
    updateState({rollbookContextStudentAssignmentProperties: {[student.id]: {[column.id]: {properties: {published: {$set: published}}}}}})
    create('homework','publishStudentAssignment')({id, published})
  }

  return (<FormControlLabel
    control={<Checkbox color="primary" checked={published || false} onChange={handleChange} />}
    label={translate(published ? 'grade-published' : 'publish-grade')}
  />)
}

function gradeCellOptionStyles() {
  return {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 100,
    color: '#4f576e',
    "& .optionsButton": {
      cursor: 'pointer',
      padding: 2,
      "&.not-assigned": {
        visibility: 'hidden'
      }
    },
  }
}
